import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import {
  getAllHomeSections,
  toggleHomeSectionActive,
  changeHomeSectionPosition,
  updateHomeSection,
} from "../../../../../../../services/homeSectionsServices";

import {
  calculateHomeSectionsStats,
  clamp,
  sortHomeSections,
  validateSectionMedia,
} from "../utils/homeSectionsSettingsUtils";

export function useHomeSectionsSettings() {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [mediaInputs, setMediaInputs] = useState({});

  const refresh = useCallback(async () => {
    try {
      setLoading(true);

      const data = await getAllHomeSections(false);
      const sorted = sortHomeSections(data);

      setSections(sorted);

      const seed = {};
      sorted.forEach((section) => {
        seed[section.id] = section.mediaURL || "";
      });

      setMediaInputs(seed);
    } catch (error) {
      console.error("Erreur chargement sections accueil :", error);
      toast.error("Impossible de charger les sections de la page d’accueil.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const stats = useMemo(() => {
    return calculateHomeSectionsStats(sections);
  }, [sections]);

  const handleToggle = async (id) => {
    setSavingId(id);

    const previous = sections;

    try {
      setSections((prev) =>
        prev.map((section) =>
          section.id === id
            ? { ...section, is_active: !section.is_active }
            : section
        )
      );

      const result = await toggleHomeSectionActive(id);

      if (!result) throw new Error("API returned empty");

      toast.success("Visibilité mise à jour.");
    } catch (error) {
      console.error("Erreur visibilité section :", error);
      setSections(previous);
      toast.error("Échec de la mise à jour de la visibilité.");
    } finally {
      setSavingId(null);
    }
  };

  const handleDragEnd = async (result) => {
    const { source, destination, draggableId } = result;

    if (!destination) return;
    if (source.index === destination.index) return;

    const previous = sections;
    const local = [...sections];

    const [moved] = local.splice(source.index, 1);
    local.splice(destination.index, 0, moved);

    setSections(local);

    try {
      const id = Number(String(draggableId).replace("sec_", ""));
      const targetPosition = destination.index + 1;

      const updatedList = await changeHomeSectionPosition(id, targetPosition);

      if (!updatedList) throw new Error("API returned empty");

      setSections(sortHomeSections(updatedList));
      toast.success("Ordre mis à jour.");
    } catch (error) {
      console.error("Erreur ordre section :", error);
      setSections(previous);
      toast.error("Échec du réordonnancement.");
      refresh();
    }
  };

  const handlePositionInput = async (id, value) => {
    const position = clamp(
      parseInt(value || "1", 10) || 1,
      1,
      sections.length
    );

    try {
      setSavingId(id);

      const updated = await changeHomeSectionPosition(id, position);

      if (!updated) throw new Error("API returned empty");

      setSections(sortHomeSections(updated));
      toast.success("Position mise à jour.");
    } catch (error) {
      console.error("Erreur position section :", error);
      toast.error("Position invalide ou erreur serveur.");
    } finally {
      setSavingId(null);
    }
  };

  const handleMediaInputChange = (id, value) => {
    setMediaInputs((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  const handleMediaSave = async (section) => {
    const id = section?.id;
    const value = String(mediaInputs[id] || "").trim();

    const errorMessage = validateSectionMedia(id, value);

    if (errorMessage) {
      toast.error(errorMessage);
      return;
    }

    try {
      setSavingId(id);

      const result = await updateHomeSection(id, {
        mediaURL: value,
      });

      if (!result) throw new Error("API returned empty");

      setSections((prev) =>
        prev.map((item) =>
          item.id === id ? { ...item, mediaURL: value } : item
        )
      );

      toast.success("Média mis à jour.");
    } catch (error) {
      console.error("Erreur média section :", error);
      toast.error("Échec de la mise à jour du média.");
    } finally {
      setSavingId(null);
    }
  };

  return {
    sections,
    loading,
    savingId,
    mediaInputs,
    stats,

    refresh,
    handleToggle,
    handleDragEnd,
    handlePositionInput,
    handleMediaInputChange,
    handleMediaSave,
  };
}